import keyBy from 'lodash/keyBy'
import map from 'lodash/map'
import pick from 'lodash/pick'

import QuestionSubtype from './domain/QuestionSubtype'
import QuestionType from './domain/QuestionType'

const FEATURE_FLAGS = {}

const ALERT_INITIAL_STATE = {
  title: '',
  description: '',
  buttons: [],
  canDismiss: false,
}

const PAGINATION_SIZE = {
  MESSAGE: 12,
  RESPONSE: 12,
}

const MATRIX_MAX_COLUMNS = 6

const READONLY_STYLE = (showDenied) =>
  showDenied ? 'cursor-not-allowed bg-gray-100' : ''

// TEAMS
const TEAM_ROLES_SELECTABLE = [
  {
    label: 'Member',
    value: 'member',
  },
  {
    label: 'Manager',
    value: 'manager',
  },
]
const TEAM_ROLES = [
  ...TEAM_ROLES_SELECTABLE,
  {
    label: 'Administrator',
    value: 'administrator',
  },
]

const TEAM_ROLE_VALUES = map(TEAM_ROLES, 'value')
const TEAM_ROLE_LABELS = map(TEAM_ROLES, 'label')

const DELIVERY_METHODS = {
  EMAIL: 'email',
  SMS: 'sms',
}

const QUESTION_TYPES = {
  text: new QuestionType({
    id: 'text',
    name: 'Text',
    formBuilderIndex: 4,
    subtypes: [
      new QuestionSubtype({ id: 'single_line', name: 'Single line text' }),
      new QuestionSubtype({ id: 'multi_line', name: 'Multi line text' }),
    ],
  }),
  email: new QuestionType({
    id: 'text',
    name: 'Email',
    formBuilderIndex: 2,
    subtypes: [
      new QuestionSubtype({
        id: 'email',
        name: 'Email address',
        label: 'Email',
      }),
    ],
    options: { idForBuilder: 'email' },
  }),
  phone: new QuestionType({
    id: 'text',
    name: 'Phone',
    formBuilderIndex: 1,
    subtypes: [
      new QuestionSubtype({
        id: 'phone',
        name: 'Phone number',
        label: 'Phone',
      }),
    ],
    options: { idForBuilder: 'phone' },
  }),
  address: new QuestionType({
    id: 'text',
    name: 'Address',
    formBuilderIndex: 3,
    subtypes: [new QuestionSubtype({ id: 'address', name: 'Address' })],
    options: { idForBuilder: 'address' },
  }),
  full_name: new QuestionType({
    id: 'text',
    name: 'Name',
    formBuilderIndex: 0,
    subtypes: [new QuestionSubtype({ id: 'full_name', name: 'Name' })],
    options: { idForBuilder: 'full_name' },
  }),
  checkbox: new QuestionType({
    id: 'checkbox',
    name: 'Checkbox',
    label: 'Choice',
    formBuilderIndex: 2,
    subtypes: [
      new QuestionSubtype({ id: 'vertical_list', name: 'Checkbox vertical' }),
      new QuestionSubtype({
        id: 'horizontal_list',
        name: 'Checkbox horizontal',
      }),
    ],
  }),
  radio: new QuestionType({
    id: 'radio',
    label: 'Choice',
    name: 'Radio',
    formBuilderIndex: 18,
    subtypes: [
      new QuestionSubtype({ id: 'vertical_list', name: 'Radio vertical' }),
      new QuestionSubtype({ id: 'horizontal_list', name: 'Radio horizontal' }),
      new QuestionSubtype({ id: 'dropdown', name: 'Dropdown' }),
      new QuestionSubtype({
        id: 'dropdown_multiple',
        name: 'Dropdown multiple',
      }),
    ],
  }),
  radio_for_builder: new QuestionType({
    id: 'radio',
    name: 'Choice',
    label: 'Choice',
    formBuilderIndex: 6,
    subtypes: [
      new QuestionSubtype({ id: 'vertical_list', name: 'Radio vertical' }),
      new QuestionSubtype({ id: 'horizontal_list', name: 'Radio horizontal' }),
    ],
    options: {
      isVisibleOnFormBuilder: true,
      idForBuilder: 'radio_for_builder',
    },
  }),
  datetime: new QuestionType({
    id: 'datetime',
    name: 'Date',
    formBuilderIndex: 8,
    subtypes: [
      new QuestionSubtype({
        id: 'date_only',
        name: 'mm/dd/yyyy',
        label: 'mm/dd/yyyy',
      }),
      new QuestionSubtype({
        id: 'month_year',
        name: 'mm/yyyy',
        label: 'mm/yyyy',
      }),
      new QuestionSubtype({
        id: 'datetime',
        name: 'mm/dd/yyyy hh:mm',
        label: 'mm/dd/yyyy hh:mm',
      }),
      new QuestionSubtype({ id: 'time_only', name: 'hh:mm', label: 'hh:mm' }),
    ],
  }),
  number: new QuestionType({
    id: 'number',
    name: 'Number',
    formBuilderIndex: 7,
    subtypes: [
      new QuestionSubtype({ id: 'number', name: 'Number' }),
      new QuestionSubtype({ id: 'scale', name: 'Scale' }),
    ],
  }),
  number_for_builder: new QuestionType({
    id: 'number',
    name: 'Number',
    formBuilderIndex: 7,
    subtypes: [new QuestionSubtype({ id: 'number', name: 'Number' })],
    options: {
      isVisibleOnFormBuilder: true,
      idForBuilder: 'number_for_builder',
    },
  }),
  file_upload: new QuestionType({
    id: 'file_upload',
    name: 'File',
    subtypes: [
      new QuestionSubtype({ id: 'file_upload', name: 'File' }),
      new QuestionSubtype({ id: 'marker', name: 'Image marker' }),
      new QuestionSubtype({ id: 'handout', name: 'Handout' }),
    ],
  }),
  file_upload_for_builder: new QuestionType({
    id: 'file_upload',
    name: 'File Upload',
    formBuilderIndex: 10,
    subtypes: [new QuestionSubtype({ id: 'file_upload', name: 'File' })],
    options: {
      isVisibleOnFormBuilder: true,
      idForBuilder: 'file_upload_for_builder',
    },
  }),
  marker_for_builder: new QuestionType({
    id: 'file_upload',
    name: 'Image Draw',
    formBuilderIndex: 14,
    subtypes: [new QuestionSubtype({ id: 'marker', name: 'Image marker' })],
    options: {
      isVisibleOnFormBuilder: true,
      idForBuilder: 'marker_for_builder',
    },
  }),
  signature: new QuestionType({
    id: 'signature',
    name: 'Signature',
    formBuilderIndex: 9,
    subtypes: [
      new QuestionSubtype({ id: 'all', name: 'Draw & Type' }),
      new QuestionSubtype({ id: 'draw', name: 'Draw' }),
      new QuestionSubtype({ id: 'type', name: 'Type' }),
    ],
  }),
  matrix: new QuestionType({
    id: 'matrix',
    name: 'Matrix',
    formBuilderIndex: 11,
    subtypes: [
      new QuestionSubtype({ id: 'radio', name: 'Radio matrix' }),
      new QuestionSubtype({ id: 'checkbox', name: 'Checkbox matrix' }),
      new QuestionSubtype({ id: 'open_ended', name: 'Open ended matrix' }),
    ],
  }),
  table: new QuestionType({
    id: 'table',
    name: 'Table',
    formBuilderIndex: 13,
    subtypes: [
      new QuestionSubtype({ id: 'open_ended', name: 'Table open ended' }),
    ],
  }),
  display_only: new QuestionType({
    id: 'display_only',
    name: 'Display only',
    subtypes: [
      new QuestionSubtype({ id: 'markdown', name: 'Markdown' }),
      new QuestionSubtype({ id: 'image', name: 'Image display' }),
    ],
  }),
  display_only_for_builder: new QuestionType({
    id: 'display_only',
    name: 'Paragraph',
    formBuilderIndex: 12,
    subtypes: [new QuestionSubtype({ id: 'markdown', name: 'Description' })],
    options: {
      isVisibleOnFormBuilder: true,
      idForBuilder: 'display_only_for_builder',
    },
  }),
  credit_card: new QuestionType({
    id: 'credit_card',
    name: 'CC on File',
    formBuilderIndex: 15,
    subtypes: [new QuestionSubtype({ id: 'stripe', name: 'Stripe' })],
    options: { idForBuilder: 'credit_card' },
  }),
  calculation: new QuestionType({
    id: 'text',
    name: 'Calculation',
    formBuilderIndex: 16,
    subtypes: [
      new QuestionSubtype({
        id: 'calculation',
        name: 'Calculation',
        label: 'Calculation',
      }),
    ],
    options: {
      idForBuilder: 'calculation',
    },
  }),
  handout: new QuestionType({
    id: 'file_upload',
    name: 'Handout',
    formBuilderIndex: 17,
    subtypes: [new QuestionSubtype({ id: 'handout', name: 'Handout' })],
    options: {
      isVisibleOnFormBuilder: true,
      idForBuilder: 'handout',
    },
  }),
  summary: new QuestionType({
    id: 'summary',
    name: 'AI Summary',
    formBuilderIndex: 18,
    subtypes: [
      new QuestionSubtype({ id: 'general', name: 'General' }),
      // new QuestionSubtype({ id: 'highlights', name: 'Highlights' }),
      // new QuestionSubtype({ id: 'diagnostic', name: 'Diagnostic' }),
      // new QuestionSubtype({ id: 'insights', name: 'Insights' }),
    ],
    options: {
      isVisibleOnFormBuilder: true,
      idForBuilder: 'summary',
    },
  }),
  divider: new QuestionType({
    id: 'divider',
    name: 'Divider',
    formBuilderIndex: 19,
    subtypes: [new QuestionSubtype({ id: 'divider', name: 'Divider' })],
    options: {
      isVisibleOnFormBuilder: true,
      idForBuilder: 'divider',
    },
  }),
}

const QUESTION_FOR_FORM_BUILDER = pick(QUESTION_TYPES, [
  'full_name',
  'phone',
  'email',
  'address',
  'text',
  'radio_for_builder',
  'number_for_builder',
  'datetime',
  'signature',
  'display_only_for_builder',
  'divider',
])

export const ADVANCED_QUESTIONS_FOR_BUILDER = pick(QUESTION_TYPES, [
  'file_upload_for_builder',
  'matrix',
  'calculation',
  'table',
  'marker_for_builder',
  'credit_card',
  'handout',
  'summary',
])

const PACKETS_FORMAT_OPTIONS = [
  {
    value: 'zip',
    label: "Download Separate PDF's in a Folder",
  },
  {
    value: 'pdf',
    label: 'Download as One PDF',
  },
]
const PACKETS_FORMAT_MAP = keyBy(PACKETS_FORMAT_OPTIONS, 'value')

export {
  ALERT_INITIAL_STATE,
  DELIVERY_METHODS,
  FEATURE_FLAGS,
  MATRIX_MAX_COLUMNS,
  PACKETS_FORMAT_MAP,
  PACKETS_FORMAT_OPTIONS,
  PAGINATION_SIZE,
  QUESTION_FOR_FORM_BUILDER,
  QUESTION_TYPES,
  READONLY_STYLE,
  TEAM_ROLE_LABELS,
  TEAM_ROLE_VALUES,
  TEAM_ROLES,
  TEAM_ROLES_SELECTABLE,
}

export const FILE_UPLOAD_QUESTION_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'txt',
  'rtf',
  'html',
  'zip',
  'mp3',
  'wma',
  'mpg',
  'flv',
  'avi',
  'jpg',
  'jpeg',
  'png',
  'gif',
]

export const TRIAL_FLOWS = {
  CREDIT_CARD_WITHOUT_CHARGE: '0',
  NO_CREDIT_CARD: '1',
  CREDIT_CARD_WITH_CHARGE: '2',
}

export const SUMMARY_DEFAULT_TEMPLATE =
  '<p><strong>Summary for [Name]</strong></p><p><br></p><p><strong>General Information:</strong></p><p>[Name] provided the following information: [General details]. This information is essential to understanding the context of the responses given throughout the form.</p><p><br></p><p><strong>Overview:</strong></p><p>The purpose of this form was to gather information regarding [Purpose of form]. [Name] has provided detailed responses regarding [Overview of topics covered in the form]. This summary highlights key aspects and insights based on the responses.</p><p><br></p><p><strong>Details and Responses:</strong></p><p>Throughout the form, [Name] shared the following insights:</p><p>- [Topic 1]: [Response related to topic 1].</p><p>- [Topic 2]: [Response related to topic 2].</p><p>- [Topic 3]: [Response related to topic 3].</p><p>Each response offers a deeper understanding of the specific areas of concern or focus for [Name].</p><p><br></p><p><strong>Key Points:</strong></p><p>Several important factors were highlighted:</p><p>- [Key Point 1]: [Details].</p><p>- [Key Point 2]: [Details].</p><p>These key points may be important for further analysis or next steps.</p><p><br></p><p><strong>Additional Information:</strong></p><p>In addition to the main topics covered, [Name] also provided further information regarding [Additional information or topics].</p><p><br></p><p>---</p><p><br></p><p><strong>Note</strong>: The data provided in this form is self-reported and should be further validated for completeness and accuracy.</p><p><br></p>'
